import Vue from 'vue';
import ViewModelBase from '@glittr/frontend-core/src/core/v2/app/viewModelBase';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';

export default class RedebitListPage extends ViewModelBase {
  get filterUploadExportStateId() {
    if (this.redebitDataSource.filter.uploadStatusExported) {
      return 1;
    }
    if (this.redebitDataSource.filter.uploadStatusExported === false) {
      return 2;
    }
    return undefined;
  }

  set filterUploadExportStateId(val) {
    if (val === 1) {
      this.redebitDataSource.filter.uploadStatusExported = true;
    } else if (val === 2) {
      this.redebitDataSource.filter.uploadStatusExported = false;
    } else {
      this.redebitDataSource.filter.uploadStatusExported = undefined;
    }
  }

  get filterPaymentExportStateId() {
    if (this.redebitDataSource.filter.paymentStatusExpored) {
      return 1;
    }
    if (this.redebitDataSource.filter.paymentStatusExpored === false) {
      return 2;
    }
    return undefined;
  }

  set filterPaymentExportStateId(val) {
    if (val === 1) {
      this.redebitDataSource.filter.paymentStatusExpored = true;
    } else if (val === 2) {
      this.redebitDataSource.filter.paymentStatusExpored = false;
    } else {
      this.redebitDataSource.filter.paymentStatusExpored = undefined;
    }
  }

  paymentDownloadMessage: { message: string, type: string } | undefined = undefined;

  selectedRedebits: number[] = [];

  uploadStates = [
    { caption: Vue.$t('page.app.redebits.list.states.1'), id: 1 },
    { caption: Vue.$t('page.app.redebits.list.states.2'), id: 2 },
    { caption: Vue.$t('page.app.redebits.list.states.3'), id: 3 },
  ];

  exportStates = [
    { caption: Vue.$t('page.app.redebits.list.exportStates.1'), id: 1 },
    { caption: Vue.$t('page.app.redebits.list.exportStates.2'), id: 2 },
  ];

  paymentStates: { id:string, caption: string }[] = [];

  redebitDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.redebits.getRedebitsList,
  });

  redebitPaymentStatusDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.redebits.getRedebitPaymentStatusLookup,
  });

  redebitDownloadUploadDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.redebits.downloadUploadStatusReport,
  });

  redebitDownloadPaymentStatusExportDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.redebits.downloadPaymentStatusReport,
  });

  async initialize(): Promise<void> {
    await Promise.allSettled([
      this.redebitDataSource.select(),
      this.redebitPaymentStatusDataSource.select(),
    ]);
    this.redebitDataSource.onSelecting.addEventListener(() => {
      this.selectedRedebits = [];
    });

    if (this.redebitPaymentStatusDataSource.data) {
      this.paymentStates = this.redebitPaymentStatusDataSource.data.items.map((i) => {
        return { id: i.data.status!, caption: this.translatePaymentState(i.data.status) };
      });
    }
    this.paymentStates.push({ id: 'pending', caption: this.translatePaymentState('pending') });
  }

  isRedebitSelected(id: number | undefined) {
    if (id) {
      return this.selectedRedebits.includes(id);
    }
    return false;
  }

  redebitToggleSelection(id: number | undefined) {
    if (id) {
      if (this.isRedebitSelected(id)) {
        const index = this.selectedRedebits.indexOf(id);
        this.selectedRedebits.splice(index, 1);
      } else {
        this.selectedRedebits.push(id);
      }
    }
  }

  toggleSelectAllRedebits() {
    if (this.selectedRedebits.length !== this.redebitDataSource.data?.items.length) {
      // select all
      this.selectedRedebits = [];
      this.redebitDataSource.data?.items.forEach((r) => {
        if (r.data.id) {
          this.selectedRedebits.push(r.data?.id);
        }
      });
      return;
    }
    // deselect all
    this.selectedRedebits = [];
  }

  async downloadUploadStatusReport() {
    this.redebitDownloadUploadDataSource.filter.redebitIds = this.selectedRedebits;
    await this.redebitDownloadUploadDataSource.select();
    if (this.redebitDownloadUploadDataSource.data) {
      const fileName = `${Vue.$date(new Date()).format('YYYYMMDDHHMM')}_RedebitUploadStatusReport.csv`;
      Vue.$service.utils.downloadFile(fileName, this.redebitDownloadUploadDataSource.data.data);
    }
    await this.redebitDataSource.select();
  }

  async downloadPaymentStatusExport() {
    this.redebitDownloadPaymentStatusExportDataSource.filter.redebitIds = this.selectedRedebits;
    await this.redebitDownloadPaymentStatusExportDataSource.select();
    const responseData = this.redebitDownloadPaymentStatusExportDataSource.data?.data;
    if (responseData && responseData.size > 0) {
      const fileName = `${Vue.$date(new Date()).format('YYYYMMDDHHMM')}_PaymentStatusReport.camt.054.xml`;
      Vue.$service.utils.downloadFile(fileName, responseData);
      this.paymentDownloadMessage = { message: Vue.$t('page.app.redebits.list.paymentdownload.success'), type: 'success' };
    } else {
      this.paymentDownloadMessage = { message: Vue.$t('page.app.redebits.list.paymentdownload.noData'), type: 'warning' };
    }
    await this.redebitDataSource.select();
  }

  statusCodeToState(statusCode?: number) {
    if (statusCode === undefined) {
      return Vue.$t('page.app.redebits.list.states.1'); // Pending
    } if (statusCode >= 200 && statusCode <= 299) {
      return Vue.$t('page.app.redebits.list.states.2'); // Success
    } if (statusCode >= 400 && statusCode <= 599) {
      return Vue.$t('page.app.redebits.list.states.3'); // Error
    }
    return Vue.$t('page.app.redebits.list.states.1'); // Pending
  }

  translatePaymentState(status?: string) {
    if (status === undefined) {
      return Vue.$tEnum('page.app.redebits.paymentstatus', 'pending');
    }
    const fallback = status.charAt(0).toUpperCase() + status.slice(1);
    return Vue.$tEnum('page.app.redebits.paymentstatus', status, fallback);
  }
}
